// src/components/MethodSheet.js
//
// "How RESET works" — the method behind the plan, for the athlete.
//
// The old "Method & calculations" modal was five grey cards of formula
// notes, two of them out of date. This is the same subject told in the
// order an athlete asks it: what the plan is built on, how it is built from
// my runs, how it changes and who decides, and what each number means.
//
// Every figure here is a figure of the methodology
// (docs/training_methodology.md) or of the engine that implements it;
// server/test/methodSheet.test.js fails when one drifts.

import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useSheetPull } from "./useSheetPull";

/* The RESET score, as server/services/metricsEngine.js calcResetScore weighs it. */
export const SCORE_WEIGHTS = [
  { key: "adherence", label: "Adherence", pct: 25, note: "sessions completed, last 28 days" },
  { key: "likelihood", label: "Goal fit", pct: 25, note: "projected finish against your target" },
  { key: "volume", label: "Volume", pct: 20, note: "kilometres run against the plan" },
  { key: "pace", label: "Pace", pct: 20, note: "prescribed paces you are hitting" },
  { key: "elevation", label: "Elevation", pct: 10, note: "vert against the plan, trail goals only" },
];

/* §Pillar 3. Shares are the middle of each range; the track is a picture of
   the shape, the text carries the ranges. */
export const PHASES = [
  { key: "base", label: "Base", range: "30–40%", share: 35, text: "Aerobic foundation. Easy volume, and the long run grows." },
  { key: "build", label: "Build", range: "30–35%", share: 32, text: "Tempo and hills arrive. The long run keeps climbing." },
  { key: "specific", label: "Specific", range: "15–25%", share: 20, text: "Race terrain, race pacing, the peak back-to-backs." },
  { key: "taper", label: "Taper", range: "10–15%", share: 13, text: "Volume drops, sharpness stays. The taper is never changed." },
];

/* §Pillar 4: the peak single long run the generator uses. */
export const LONG_RUN_PEAKS = [["50K", 31], ["50 miles", 44], ["100K", 50], ["100 miles", 55]];

/* §Adaptation Rules R1–R5, in the order they are checked. */
export const RULES = [
  ["Form deep in the red (TSB −25 or lower)", "Next week −20%, hard sessions become easy"],
  ["Sessions missed and pace slowing", "Next two weeks −15%, quality paused"],
  ["Easy pace drifting more than 5%", "Next week −15%, tempo becomes easy"],
  ["Fewer than 70% of sessions done", "Next two weeks −10%, one hard session dropped"],
  ["More than 120% of the plan so far this week", "Hold at plan, no bonus volume"],
];

const SECTIONS = [["ideas", "Ideas"], ["yours", "Your plan"], ["adapt", "Adapting"], ["numbers", "Numbers"]];

/* The long-run curve of §Pillar 3, drawn over the phases it belongs to:
   45% of peak in week one, 100% in the middle of the Specific block, eased
   back to 85% for the last training week, then the taper's 55 / 40 / 25.
   `bounds` are the phase boundaries in percent of the plan. */
function LongRunCurve({ bounds }) {
  const [b1, b2, b3] = bounds;
  const tap = (100 - b3) / 3;
  const pts = [[0, 45], [b1, 68], [b2, 88], [(b2 + b3) / 2, 100], [Math.max(b3 - 1, (b2 + b3) / 2 + 1), 85], [b3 + tap * 0.5, 55], [b3 + tap * 1.5, 40], [100, 25]];
  const x = (p) => (p / 100) * 300;
  const y = (v) => 62 - (v / 100) * 54;
  const d = pts.map((p, i) => `${i ? "L" : "M"}${x(p[0]).toFixed(1)} ${y(p[1]).toFixed(1)}`).join(" ");
  return (
    <svg className="mtCurve" viewBox="0 0 300 66" preserveAspectRatio="none" aria-hidden="true">
      <path className="mtCurve__area" d={`${d} L300 66 L0 66 Z`} />
      <path className="mtCurve__line" d={d} />
      <circle className="mtCurve__peak" cx={x(pts[3][0])} cy={y(100)} r="3" />
    </svg>
  );
}

export default function MethodSheet({ onClose, now }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef: null });
  const [active, setActive] = useState("ideas");
  const secRefs = useRef({});

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeRef.current && closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // The chip of the section being read lights up.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const top = body.getBoundingClientRect().top + 72;
        let cur = SECTIONS[0][0];
        for (const [key] of SECTIONS) {
          const el = secRefs.current[key];
          if (el && el.getBoundingClientRect().top <= top) cur = key;
        }
        // The last section can be too short to reach the line: the end of the scroll is the last section.
        if (body.scrollTop + body.clientHeight >= body.scrollHeight - 4) cur = SECTIONS[SECTIONS.length - 1][0];
        setActive(cur);
      });
    };
    body.addEventListener("scroll", onScroll, { passive: true });
    return () => { body.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const jump = (key) => {
    const el = secRefs.current[key];
    const body = bodyRef.current;
    if (!el || !body) return;
    const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    body.scrollTo({ top: Math.max(0, el.offsetTop - 56), behavior: reduce ? "auto" : "smooth" });
    setActive(key);
  };
  const sec = (key) => (el) => { secRefs.current[key] = el; };
  const here = now && now.totalWeeks > 0 ? Math.max(0, Math.min(100, ((now.weekIdx - 0.5) / now.totalWeeks) * 100)) : null;
  /* The athlete's own plan when there is one — its real phase lengths, so
     the "you are here" marker sits in the phase they are actually in — and
     the methodology's typical shape otherwise. */
  const own = now && now.shares && PHASES.every((p) => now.shares[p.key] > 0) ? now.shares : null;
  const total = PHASES.reduce((a, p) => a + (own ? own[p.key] : p.share), 0);
  const shareOf = (p) => ((own ? own[p.key] : p.share) / total) * 100;
  const bounds = [shareOf(PHASES[0]), shareOf(PHASES[0]) + shareOf(PHASES[1]), shareOf(PHASES[0]) + shareOf(PHASES[1]) + shareOf(PHASES[2])];

  return (
    <div className="bottomSheetOverlay" ref={overlayRef} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="bottomSheetPanel tsSheet mtSheet" style={{ "--type": "var(--accent-base)" }} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="How RESET works">
        <div className="bottomSheetDragHandle" aria-hidden="true" />
        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">How RESET works</div>
            <div className="tsHead__date"><span className="tsHead__when">The method</span><span className="tsHead__sep" />behind your plan</div>
          </div>
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>

        <div ref={bodyRef} className="bottomSheetBody tsBody mtBody">
          <nav className="mtNav" aria-label="Sections">
            {SECTIONS.map(([key, label]) => (
              <button key={key} type="button" className={"mtNav__chip" + (active === key ? " is-on" : "")} aria-current={active === key ? "true" : undefined} onClick={() => jump(key)}>{label}</button>
            ))}
          </nav>

          <p className="mtLead">
            Your plan is built on four ideas that hold up in research and in the work of the coaches who train the best ultra runners. Every session serves at least one of them. A session that serves none is not in the plan.
          </p>

          {/* ───────── The ideas ───────── */}
          <section ref={sec("ideas")} className="mtSec" data-sec="ideas">
            <h3 className="mtSec__label">The four ideas</h3>

            <article className="mtIdea" style={{ "--idea": "var(--c-easy, var(--accent))" }}>
              <header className="mtIdea__head"><span className="mtIdea__n">1</span><h4 className="mtIdea__title">Mostly easy, sometimes hard</h4></header>
              <div className="mtSplit" role="img" aria-label="80 percent easy, 20 percent hard">
                <span className="mtSplit__easy" style={{ flexGrow: 80 }}><b>80%</b> easy</span>
                <span className="mtSplit__hard" style={{ flexGrow: 20 }}><b>20%</b></span>
              </div>
              <p className="mtP">About 80% of your running time is easy enough to hold a conversation. About 20% is properly hard: tempo, hills, intervals. Almost nothing sits in between.</p>
              <p className="mtP mtP--quiet">Most runners drift into that middle. Easy days a little too fast, hard days a little too soft. It feels like work and builds very little. Easy and long runs are your 80. Tempo is your 20.</p>
            </article>

            <article className="mtIdea" style={{ "--idea": "var(--c-long, var(--info-base))" }}>
              <header className="mtIdea__head"><span className="mtIdea__n">2</span><h4 className="mtIdea__title">Long, then long again</h4></header>
              <div className="mtPair" role="img" aria-label="Day one is 60 to 70 percent of day two">
                <div className="mtPair__col"><span className="mtPair__bar" style={{ height: 40 }} /><span className="mtPair__l">Day 1</span><span className="mtPair__v">60–70%</span></div>
                <div className="mtPair__col"><span className="mtPair__bar is-long" style={{ height: 62 }} /><span className="mtPair__l">Day 2</span><span className="mtPair__v">The long run</span></div>
              </div>
              <p className="mtP">From 50K up, the long run comes the day after a medium-long one. Running long on tired legs is exactly what an ultra asks of you.</p>
              <p className="mtP mtP--quiet">Two days together build more than the same hours spread across the week. Both days are easy. The combined distance matters, not the speed.</p>
            </article>

            <article className="mtIdea" style={{ "--idea": "var(--caution)" }}>
              <header className="mtIdea__head"><span className="mtIdea__n">3</span><h4 className="mtIdea__title">The plan becomes the race</h4></header>
              <div className="mtPhases">
                <LongRunCurve bounds={bounds} />
                <div className="mtPhases__track">
                  {PHASES.map((p) => <span key={p.key} className={`mtPhases__seg mtPhases__seg--${p.key}`} style={{ flexGrow: shareOf(p), flexBasis: 0 }}>{p.label}</span>)}
                  {own && here != null ? <i className="mtPhases__here" style={{ left: `${here}%` }} aria-hidden="true" /> : null}
                </div>
                <div className="mtPhases__cap">
                  <span>{own ? "The long run over your plan" : "The long run over a plan"}</span>
                  {now && now.totalWeeks > 0 ? <span className="mtPhases__now">You are in week {now.weekIdx} of {now.totalWeeks}{now.phaseLabel ? `, ${now.phaseLabel}` : ""}</span> : null}
                </div>
              </div>
              <p className="mtP">A plan does not just get harder. It gets more like your race, in four phases.</p>
              <dl className="mtRows">
                {PHASES.map((p) => (
                  <div key={p.key} className="mtRows__row">
                    <dt><i className={`mtDot mtDot--${p.key}`} aria-hidden="true" />{p.label}<em>{p.range}</em></dt>
                    <dd>{p.text}</dd>
                  </div>
                ))}
              </dl>
              <p className="mtP mtP--quiet">Every fourth week in base and build drops by 25%. That week is not a break from training. It is when the fitness is built.</p>
            </article>

            <article className="mtIdea" style={{ "--idea": "var(--race-ultra, var(--viz-race))" }}>
              <header className="mtIdea__head"><span className="mtIdea__n">4</span><h4 className="mtIdea__title">Never the whole race in one run</h4></header>
              <div className="mtCap" role="img" aria-label="The longest single run is at most 75 percent of race distance">
                <div className="mtCap__bar"><span className="mtCap__fill" /><i className="mtCap__mark" /></div>
                <div className="mtCap__legend"><span>Longest single run</span><span><b>75%</b> of race distance</span></div>
              </div>
              <p className="mtP">Your longest single run tops out at 75% of race distance. The back-to-back covers the rest, at a fraction of the damage.</p>
              <div className="mtPeaks">
                {LONG_RUN_PEAKS.map(([race, km]) => (
                  <div key={race} className="mtPeaks__cell"><span className="mtPeaks__v">{km}<u>km</u></span><span className="mtPeaks__l">{race}</span></div>
                ))}
              </div>
              <p className="mtP mtP--quiet">The peak long run for each distance. Hiking and vertical sessions appear only when your race has real climbing; a flat ultra never gets them by default.</p>
            </article>
          </section>

          {/* ───────── Your plan ───────── */}
          <section ref={sec("yours")} className="mtSec" data-sec="yours">
            <h3 className="mtSec__label">Built on your runs</h3>
            <div className="mtFacts">
              <div className="mtFact">
                <h4 className="mtFact__title">It starts from what you actually run</h4>
                <p className="mtP">Not from a number you type in. RESET reads your Strava history: the median of your last four full weeks, your longest run in 60 days, and a threshold pace from your fastest recent runs of 3 to 15 km.</p>
              </div>
              <div className="mtFact">
                <h4 className="mtFact__title">A ramp you can survive</h4>
                <div className="mtRamp" aria-hidden="true">{[38, 42, 46, 35, 51, 56, 62, 46, 68, 75, 82, 62].map((h, i) => <i key={i} className={i % 4 === 3 ? "is-rec" : ""} style={{ height: `${h}%` }} />)}</div>
                <p className="mtP">Volume rises about 10% a week toward the peak for your distance. If the time left cannot get you there from where you are, RESET says so before it builds anything, and offers three honest ways out: a later race, a shorter distance, or a plan built to finish.</p>
              </div>
              <div className="mtFact">
                <h4 className="mtFact__title">Paces you can run</h4>
                <p className="mtP">Every prescribed pace comes from your measured threshold pace. Without one it is projected from your goal time, and a projection more than 15% faster than you actually run is refused.</p>
              </div>
            </div>
          </section>

          {/* ───────── Adapting ───────── */}
          <section ref={sec("adapt")} className="mtSec" data-sec="adapt">
            <h3 className="mtSec__label">It adapts. You decide.</h3>
            <p className="mtP">Each week RESET reads your numbers against the plan. When they call for a change, it proposes one. Nothing changes until you accept it.</p>
            <div className="mtRules">
              <div className="mtRules__head"><span>When it sees</span><span>It proposes</span></div>
              {RULES.map(([when, then], i) => (
                <div key={i} className="mtRules__row"><span>{when}</span><span>{then}</span></div>
              ))}
            </div>
            <p className="mtP mtP--quiet">The first rule that applies wins; they never stack. RESET never proposes more volume on its own: running faster than the plan is not proof that more is safe.</p>
            <div className="mtCall">
              <h4 className="mtCall__title">More is your call</h4>
              <p className="mtP">Ask the coach for more, less, longer, shorter, a move or a skip. It puts exactly what you asked on your screen, each session before and after, with what it costs in your own numbers. It does not refuse and it does not bargain.</p>
              <p className="mtP mtP--quiet">Four things no request changes: sessions already behind you, the race itself, the taper, and a single run above the cap.</p>
            </div>
          </section>

          {/* ───────── The numbers ───────── */}
          <section ref={sec("numbers")} className="mtSec" data-sec="numbers">
            <h3 className="mtSec__label">What the numbers mean</h3>

            <div className="mtNum">
              <h4 className="mtNum__title">RESET score</h4>
              <div className="mtScore" role="img" aria-label={SCORE_WEIGHTS.map((w) => `${w.label} ${w.pct} percent`).join(", ")}>
                {SCORE_WEIGHTS.map((w) => <span key={w.key} className={`mtScore__seg mtScore__seg--${w.key}`} style={{ flexGrow: w.pct }} />)}
              </div>
              <dl className="mtRows mtRows--score">
                {SCORE_WEIGHTS.map((w) => (
                  <div key={w.key} className="mtRows__row">
                    <dt><i className={`mtDot mtDot--${w.key}`} aria-hidden="true" />{w.label}<em>{w.pct}%</em></dt>
                    <dd>{w.note}</dd>
                  </div>
                ))}
              </dl>
              <p className="mtP mtP--quiet">65 and above is on track. 40 to 65 is building. Below 40 needs attention. On a road goal the elevation share is spread across the other four.</p>
              <p className="mtP mtNote">The score rewards doing what the plan says, not doing more than it says. Running past 110% of the plan lowers the volume part. If you want more, raise the plan through the coach, then hit it.</p>
            </div>

            <div className="mtNum">
              <h4 className="mtNum__title">Adherence</h4>
              <p className="mtP">The share of planned sessions you completed over the last 28 days. A run counts for the session on the same day, or one day either side. A run cut short counts in part.</p>
            </div>

            <div className="mtNum">
              <h4 className="mtNum__title">Weekly load</h4>
              <div className="mtBands" role="img" aria-label="Under below 70 percent, on track from 70 to 120 percent, over above 120 percent">
                <span className="mtBands__seg is-under" style={{ flexGrow: 70 }}>Under</span>
                <span className="mtBands__seg is-on" style={{ flexGrow: 50 }}>On track</span>
                <span className="mtBands__seg is-over" style={{ flexGrow: 30 }}>Over</span>
              </div>
              <div className="mtBands__scale" aria-hidden="true"><span style={{ left: "46.7%" }}>70%</span><span style={{ left: "80%" }}>120%</span></div>
              <p className="mtP">This week's kilometres against what was due so far, never against the whole week. Before anything is due, nothing is behind.</p>
            </div>

            <div className="mtNum">
              <h4 className="mtNum__title">Pace fit</h4>
              <p className="mtP">How far your paces sat from the prescribed ones over the last 14 days. Within 5 seconds per kilometre is on pace. Tempo and long runs weigh most. An easy run that came out a little quick still counts as on pace.</p>
            </div>

            <div className="mtNum">
              <h4 className="mtNum__title">Plan trajectory</h4>
              <p className="mtP">Where your goal time is heading, from pace and adherence together. The worse of the two sets the verdict. With fewer than half the sessions done, the projection is never shown faster than your goal.</p>
            </div>

            <div className="mtNum">
              <h4 className="mtNum__title">Fitness and form</h4>
              <dl className="mtRows">
                <div className="mtRows__row"><dt>Fitness</dt><dd>Your training load averaged over six weeks.</dd></div>
                <div className="mtRows__row"><dt>Fatigue</dt><dd>The same, over the last week.</dd></div>
                <div className="mtRows__row"><dt>Form</dt><dd>Fitness minus fatigue.</dd></div>
              </dl>
              <div className="mtForm" role="img" aria-label="Form bands from overreaching to peaked">
                <div className="mtForm__scale" aria-hidden="true"><i className="is-over" /><i className="is-fat" /><i className="is-bal" /><i className="is-fresh" /><i className="is-peak" /></div>
                <div className="mtForm__row is-peak"><span>Peaked</span><em>above +25</em></div>
                <div className="mtForm__row is-fresh"><span>Fresh</span><em>+5 to +25</em></div>
                <div className="mtForm__row is-bal"><span>Balanced</span><em>−10 to +5</em></div>
                <div className="mtForm__row is-fat"><span>Fatigued</span><em>−25 to −10</em></div>
                <div className="mtForm__row is-over"><span>Overreaching</span><em>below −25</em></div>
              </div>
              <p className="mtP mtP--quiet">Climbing counts. Load is measured on grade-adjusted pace, so a slow uphill kilometre is not read as an easy one.</p>
            </div>

            <div className="mtNum">
              <h4 className="mtNum__title">What RESET does not do</h4>
              <p className="mtP">It does not prescribe by heart rate. Pace and effort prescribe; heart rate checks the result. Strength work, nutrition, heat and altitude blocks are yours to run alongside.</p>
            </div>

            <p className="mtSources">
              Built on the work of Jason Koop (Training Essentials for Ultrarunning), David and Megan Roche (The Happy Runner), Krissy Moehl (Running Your First Ultra), Stephen Seiler's research on polarised training, and Allen and Coggan's performance manager model.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
